// src/pages/PointsBuy.tsx
import React, { useEffect, useMemo, useState } from "react";
import Header from "components/Header";
import Footer from "components/Footer";
import {
  Box,
  Paper,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useMe } from "../contexts/MeContext";

type PointRow = { id: number | string; point: number; priceJPY: number };

// 🔓 公開ポイントマスタ API（フロントからは常に相対パス）
const PUBLIC_POINTS_API = "/api/public/points";

// 🔗 銀行口座の外部リンク（ネットバンキングなど）
// .env に REACT_APP_BANK_EXTERNAL_URL を入れておけばそこに飛ばす
const BANK_EXTERNAL_URL = process.env.REACT_APP_BANK_EXTERNAL_URL || "";


const JPY = new Intl.NumberFormat("ja-JP");

// ===== Telecom Credit（都度決済）設定 =====
// 32208 = BOAT億MAN 本番サイト用クライアントIP
// 00283 は検証用として残す場合はコメントでメモしておく
const TELECOM_CLIENT_IP = "32208";



// クレジットカード決済（都度決済）
const TELECOM_ORDER_BASE =
  "https://secure.telecomcredit.co.jp/inetcredit/secure/order.pl";

function buildTelecomUrl(money: number, sendid: string) {
  const params = new URLSearchParams({
    clientip: TELECOM_CLIENT_IP,
    money: String(money),
    sendid: String(sendid),
  });
  return `${TELECOM_ORDER_BASE}?${params.toString()}`;
}

// 銀行振込決済（口座発行型）
const TELECOM_BANK_BASE =
  "https://secure.telecomcredit.co.jp/banktransfer/settle/transfer_request.pl";

function buildTelecomBankUrl(money: number, sendid: string) {
  const params = new URLSearchParams({
    clientip: TELECOM_CLIENT_IP,
    money: String(money),
    sendid: String(sendid),
  });
  return `${TELECOM_BANK_BASE}?${params.toString()}`;
}

export default function PointsBuy() {
  const [rows, setRows] = useState<PointRow[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    sever: "success" | "error";
  }>({ open: false, msg: "", sever: "success" });
  const [loading, setLoading] = useState(false);

  // 決済前確認モーダル
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMethod, setConfirmMethod] = useState<"credit" | "bank" | null>(
    null
  );

  // ログイン中ユーザー情報（sendid に使用）
  const { me } = useMe();

   // ポイントリスト取得（公開 API から取得）
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(PUBLIC_POINTS_API);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const normalized: PointRow[] = (data || []).map((r: any, i: number) => ({
          id: String(r.id ?? i),
          point: Number(r.point ?? r.points ?? 0),
          priceJPY: Number(r.priceJPY ?? r.amount ?? r.price ?? 0),
        }));
        const sorted = normalized.sort((a, b) => a.point - b.point);
        setRows(sorted);
        if (sorted.length) setSelectedId(String(sorted[0].id));
      } catch (e: any) {
        setToast({
          open: true,
          msg: `ポイント取得失敗：${e?.message ?? "Failed to fetch"}`,
          sever: "error",
        });
      }
    })();
  }, []);


  const selected = useMemo(
    () => rows.find((r) => String(r.id) === selectedId) || null,
    [rows, selectedId]
  );

  const selectedLabel = useMemo(() => {
    if (!selected) return "";
    return `${JPY.format(selected.point)} ポイント = ${JPY.format(
      selected.priceJPY
    )} 円`;
  }, [selected]);

  // 実際のクレジット決済開始（テレコムクレジットの注文ページへリダイレクト）
  const startCredit = () => {
    if (!selected) return;

    if (!me) {
      setToast({
        open: true,
        msg: "ログイン情報を取得できません。再ログインしてください。",
        sever: "error",
      });
      return;
    }

    const userId =
      (me as any).userId || (me as any).id || (me as any).lineId || "";

    if (!userId) {
      setToast({
        open: true,
        msg: "会員IDが取得できません。サポートへお問い合わせください。",
        sever: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const url = buildTelecomUrl(selected.priceJPY, String(userId));
      // テレコムクレジットの決済画面へ遷移
      window.location.href = url;
    } catch (e: any) {
      setToast({
        open: true,
        msg: `クレジット決済開始エラー：${e?.message ?? ""}`,
        sever: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // 実際の銀行振込開始（テレコムクレジットの銀行振込画面へ遷移）
  const startBank = () => {
    if (!selected) return;

    if (!me) {
      setToast({
        open: true,
        msg: "ログイン情報を取得できません。再ログインしてください。",
        sever: "error",
      });
      return;
    }

    const userId =
      (me as any).userId || (me as any).id || (me as any).lineId || "";

    if (!userId) {
      setToast({
        open: true,
        msg: "会員IDが取得できません。サポートへお問い合わせください。",
        sever: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const url = buildTelecomBankUrl(selected.priceJPY, String(userId));
      // テレコムクレジットの銀行振込決済画面へ遷移
      window.location.href = url;
    } catch (e: any) {
      setToast({
        open: true,
        msg: `銀行振込決済開始エラー：${e?.message ?? ""}`,
        sever: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ボタンクリック時：まず確認モーダルを開く
  const handleCreditClick = () => {
    if (!selected) return;
    setConfirmMethod("credit");
    setConfirmOpen(true);
  };

  const handleBankClick = () => {
    if (!selected) return;
    setConfirmMethod("bank");
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmMethod(null);
  };

  const handleConfirmProceed = async () => {
    if (confirmMethod === "credit") {
      // クレジットカード → テレコムの決済画面へ
      startCredit();
    } else if (confirmMethod === "bank") {
      // 銀行振込 → テレコムの銀行振込画面へ
      startBank();
    }
    handleConfirmClose();
  };

  return (
    <main className="member">
      <Header />

      <Box sx={{ maxWidth: 920, mx: "auto", p: { xs: 1.5, md: 3 } }}>
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardHeader
            title={<Typography fontWeight={800}>ポイントのご購入手続き</Typography>}
            subheader={
              <Typography variant="body2" color="text.secondary">
                追加購入分のポイント数を選択し、決済方法をお選びください。
              </Typography>
            }
          />
          <CardContent>
            {/* STEP1 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800}>
                  STEP1. 追加するポイントを選択してください
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="point-select-label">ポイント選択</InputLabel>
                  <Select
                    labelId="point-select-label"
                    label="ポイント選択"
                    value={selectedId}
                    onChange={(e) => setSelectedId(String(e.target.value))}
                  >
                    {rows.map((r) => (
                      <MenuItem key={r.id} value={String(r.id)}>
                        {`${JPY.format(r.point)} ポイント = ${JPY.format(
                          r.priceJPY
                        )} 円`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            {/* STEP2 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={800}>
                  STEP2. 決済方法を選択してください
                </Typography>

                {/* クレジットカード */}
                <Stack spacing={1.25}>
                  <Box
                    component="img"
                    src="/assets/member/credit.webp"
                    alt="利用できるクレジットカード"
                    sx={{
                      width: "100%",
                      maxWidth: 520,
                      height: "auto",
                      borderRadius: 1.5,
                      alignSelf: { xs: "stretch", sm: "flex-start" },
                      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                    }}
                    loading="lazy"
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCreditClick}
                    disabled={!selected || loading}
                    sx={{ maxWidth: 520 }}
                  >
                    クレジットカードで決済する
                  </Button>
                </Stack>

                {/* 銀行振込 */}
                <Stack spacing={1.25} mt={1.5}>
                  <Box
                    component="img"
                    src="/assets/member/bank.webp"
                    alt="銀行振込決済"
                    sx={{
                      width: "100%",
                      maxWidth: 520,
                      height: "auto",
                      borderRadius: 1.5,
                      alignSelf: { xs: "stretch", sm: "flex-start" },
                      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                    }}
                    loading="lazy"
                  />
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleBankClick}
                    disabled={!selected || loading}
                    sx={{ maxWidth: 520 }}
                  >
                    銀行振込で決済する
                  </Button>

                  {/* 🔗 銀行口座の外部リンク（あれば表示） */}
                  {BANK_EXTERNAL_URL && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      インターネットバンキングをご利用の方は{" "}
                      <Box
                        component="a"
                        href={BANK_EXTERNAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: "underline",
                          fontWeight: 600,
                        }}
                      >
                        こちら
                      </Box>
                      からお手続きください。
                    </Typography>
                  )}
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  決済完了後、システムの状況によりポイント反映までお時間をいただく場合があります。ご了承ください。
                </Typography>
              </Stack>
            </Paper>
          </CardContent>
        </Card>
      </Box>

      <Footer />

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast.sever} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* 決済前確認モーダル */}
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 18 }}>
          ポイント購入内容の確認
        </DialogTitle>
        <DialogContent dividers>
          <Typography fontSize={14} fontWeight={700} mb={0.5}>
            決済方法
          </Typography>
          <Typography fontSize={14} mb={1.5}>
            {confirmMethod === "credit"
              ? "クレジットカード決済でポイントを追加します。"
              : confirmMethod === "bank"
              ? "銀行振込決済でポイントを追加します。"
              : ""}
          </Typography>

          <Typography fontSize={14} fontWeight={700} mb={0.5}>
            ご購入ポイント
          </Typography>
          <Typography fontSize={14} mb={1.5}>
            {selectedLabel || "-"}
          </Typography>

          <Typography fontSize={11} color="text.secondary" lineHeight={1.6}>
            決済完了後のポイント反映は、システムの処理状況により前後する場合があります。
            <br />
            情報商材・デジタルコンテンツの性質上、決済完了後のキャンセル・返金・返品には
            ご対応できませんので、内容をご確認のうえ決済にお進みください。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleConfirmClose}>戻る</Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            disabled={loading || !selected || !confirmMethod}
          >
            決済に進む
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
